import { API_BASE_URL } from './api';
import { authService } from './authService';
import {
  ApneaContext,
  ApneaPlanDto,
  ApneaProfileDto,
  ApneaSessionDto,
  SaveApneaSessionRequest,
} from '../types/Apnea';

export * from '../types/Apnea';

class ApneaService {
  private async request<T>(path: string, init?: RequestInit): Promise<T> {
    const response = await fetch(`${API_BASE_URL}/apnea${path}`, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        ...authService.getAuthHeaders(),
        ...init?.headers,
      },
    });

    if (response.status === 403) {
      throw new Error('Apnea training is not enabled for your account.');
    }
    if (!response.ok) {
      throw new Error(`Request failed (${response.status})`);
    }
    return response.json() as Promise<T>;
  }

  /** Recent sessions, active plan and profile in one round trip. */
  async getContext(days = 30): Promise<ApneaContext> {
    return this.request<ApneaContext>(`/context?days=${days}`);
  }

  async getSessions(options: { since?: Date; limit?: number } = {}): Promise<ApneaSessionDto[]> {
    const params = new URLSearchParams();
    if (options.since) params.set('since', options.since.toISOString());
    if (options.limit) params.set('limit', String(options.limit));
    const query = params.toString();
    const result = await this.request<{ sessions: ApneaSessionDto[] }>(
      `/sessions${query ? `?${query}` : ''}`
    );
    return result.sessions;
  }

  async saveSession(session: SaveApneaSessionRequest): Promise<ApneaSessionDto> {
    return this.request<ApneaSessionDto>('/sessions', {
      method: 'POST',
      body: JSON.stringify(session),
    });
  }

  async deleteSession(sortKey: string): Promise<void> {
    await this.request<{ success: boolean }>(`/sessions/${encodeURIComponent(sortKey)}`, {
      method: 'DELETE',
    });
  }

  async getPlans(): Promise<ApneaPlanDto[]> {
    return this.request<ApneaPlanDto[]>('/plans');
  }

  async savePlan(plan: Partial<ApneaPlanDto>): Promise<ApneaPlanDto> {
    return this.request<ApneaPlanDto>('/plans', {
      method: 'POST',
      body: JSON.stringify(plan),
    });
  }

  async getProfile(): Promise<ApneaProfileDto> {
    return this.request<ApneaProfileDto>('/profile');
  }

  async saveProfile(profile: Partial<ApneaProfileDto>): Promise<ApneaProfileDto> {
    return this.request<ApneaProfileDto>('/profile', {
      method: 'POST',
      body: JSON.stringify(profile),
    });
  }
}

export const apneaService = new ApneaService();
